import React, { useCallback, useMemo } from "react";
import { Dropdown, IconButton, Text } from "@fluentui/react";
import type { IDropdownOption } from "@fluentui/react";
import styles from "@/components/telemetry/common/PaginationFooter.module.css";

type PageSize = 10 | 25 | 50;

const PAGE_SIZE_OPTIONS: PageSize[] = [10, 25, 50];

function isPageSize(value: number): value is PageSize {
  return PAGE_SIZE_OPTIONS.some((size) => size === value);
}

interface PaginationFooterProps {
  pageNumber: number;
  pageSize: PageSize;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: PageSize) => void;
}

function PaginationFooter({
  pageNumber,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
}: PaginationFooterProps) {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const startItem = totalCount === 0 ? 0 : (pageNumber - 1) * pageSize + 1;
  const endItem = Math.min(pageNumber * pageSize, totalCount);

  const pageSizeOptions: IDropdownOption[] = useMemo(
    () => PAGE_SIZE_OPTIONS.map((size) => ({ key: size, text: String(size) })),
    [],
  );

  const handlePageSizeChange = useCallback(
    (_: unknown, option?: IDropdownOption) => {
      if (option) {
        const numKey = Number(option.key);
        if (isPageSize(numKey)) onPageSizeChange(numKey);
      }
    },
    [onPageSizeChange],
  );

  const handlePrev = useCallback(() => {
    if (pageNumber > 1) onPageChange(pageNumber - 1);
  }, [pageNumber, onPageChange]);

  const handleNext = useCallback(() => {
    if (pageNumber < totalPages) onPageChange(pageNumber + 1);
  }, [pageNumber, totalPages, onPageChange]);

  return (
    <div className={styles.footer}>
      <div className={styles["footer-left"]}>
        <Text variant="small" className={styles.label}>
          Items per page
        </Text>
        <Dropdown
          options={pageSizeOptions}
          selectedKey={pageSize}
          onChange={handlePageSizeChange}
          styles={{ dropdown: { width: 70 }, root: { display: "inline-flex" } }}
          ariaLabel="Items per page"
        />
      </div>
      <div className={styles["footer-center"]}>
        <Text variant="small">
          {totalCount === 0
            ? "No items"
            : `Showing ${startItem}\u2013${endItem} of ${totalCount} items`}
        </Text>
      </div>
      <div className={styles["footer-right"]}>
        <IconButton
          iconProps={{ iconName: "ChevronLeft" }}
          title="Previous page"
          ariaLabel="Previous page"
          disabled={pageNumber <= 1}
          onClick={handlePrev}
          styles={{ root: { width: 32, height: 32 } }}
        />
        <Text variant="small" className={styles["page-text"]}>
          Page {pageNumber} of {totalPages}
        </Text>
        <IconButton
          iconProps={{ iconName: "ChevronRight" }}
          title="Next page"
          ariaLabel="Next page"
          disabled={pageNumber >= totalPages}
          onClick={handleNext}
          styles={{ root: { width: 32, height: 32 } }}
        />
      </div>
    </div>
  );
}

export default PaginationFooter;
export type { PageSize };
